import { Button, Tooltip, IconButton } from '@mui/material';
import T from 'i18n';
import { FiberManualRecord, KeyboardDoubleArrowLeft, ContentCopy } from '@mui/icons-material';
import TypingEffect from 'features/common/TypingEffect';
import React, { createRef, useRef, useState } from 'react';
import history from 'common/history';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentSelectedFile } from '../redux/setCurrentSelectedFile';
import { fetchFileDetails } from '../redux/fetchFileDetails';
import { setDocSearch } from 'features/viewing/redux/setDocSearch';
import { selectCurrentCaseId } from 'common/selectors';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface SearchAnswerContainerProps {
  response: any;
  typingEffect: boolean;
  scrollBarRef: any;
  handleMatchesLinkClicked?: any;
  allMatchesCount: number;
  allMatches?: any;
  isTranscriptPage?: boolean;
  fileSidebarAskAI?: boolean;
  viewerControl?: any;
  rawAnswer?: string;
  gridView?: any;
}

const SearchAnswerContainer = ({
  response,
  rawAnswer,
  typingEffect,
  scrollBarRef,
  handleMatchesLinkClicked,
  allMatchesCount,
  allMatches,
  isTranscriptPage,
  fileSidebarAskAI,
  viewerControl,
  gridView,
}: SearchAnswerContainerProps) => {
  const dispatch = useDispatch();
  const caseId = useSelector(selectCurrentCaseId);
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [clickedSource, setClickedSource] = useState<string | null>(null);

  // Always call useRef unconditionally, then determine the content based on rawAnswer
  const textRefs = useRef<React.RefObject<HTMLDivElement>[]>(
    !rawAnswer ? response.map(() => createRef()) : [],
  );

  // Cache for storing fetchFileDetails results to prevent repetitive API calls
  const fileDetailsCache = useRef<{ [key: string]: any }>({});

  // Create table ID at the component level
  const tableId = React.useMemo(() => `table-${Math.random().toString(36).substr(2, 9)}`, []);

  // Create styles at the component level
  React.useEffect(() => {
    const styleId = 'markdown-table-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .markdown-table tbody tr:nth-child(even) {
          background-color: #E5E5E5 !important;
        }
        .markdown-table tbody tr:nth-child(odd) {
          background-color: transparent !important;
        }
        .table-container {
          position: relative;
          display: inline-block;
        }
        .table-copy-button {
          position: absolute;
          top: 0px;
          right: 0px;
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(4px);
          border: 1px solid #ddd;
          border-radius: 4px;
          z-index: 9999999;
        }
        .table-container:hover .table-copy-button {
          opacity: 1;
        }
        .table-copy-button:hover {
          background: rgba(255, 255, 255, 1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Reset clicked source when response or rawAnswer changes (new search)
  React.useEffect(() => {
    setClickedSource(null);
  }, [response, rawAnswer]);

  // Limit the number of items to render to prevent excessive scrolling
  const MAX_ITEMS_TO_RENDER = 50;
  const itemsToRender = !rawAnswer ? response.slice(0, MAX_ITEMS_TO_RENDER) : [];
  const hasMoreItems = !rawAnswer ? response.length > MAX_ITEMS_TO_RENDER : [];

  const selectText = (index: number) => {
    const textElement = textRefs.current[index].current;
    if (textElement) {
      if (window.getSelection) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(textElement);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  const highlightText = (text: string, highlight: string) => {
    if (!highlight) return text;
    const regex = new RegExp(highlight, 'gi');
    const matches = text.match(regex);
    if (matches) {
      return text.replace(regex, `<span style="background-color: #ffff00">${matches[0]}</span>`);
    }
    return text;
  };

  // Function to copy table content to clipboard
  const copyTableToClipboard = async (tableElement: HTMLTableElement, rawMarkdown?: string) => {
    try {
      // Get the HTML content of the table
      const tableHTML = tableElement.outerHTML;

      // Get markdown format for fallback
      let markdownText = '';

      // If we have raw markdown text, try to extract the table portion
      if (rawMarkdown) {
        // Find table markdown in the raw text - more comprehensive regex
        const tableRegex = /\|[^\n]*\|[\s\S]*?(?=\n\n|\n(?!\|)|\n$|$)/g;
        const tableMatches = rawMarkdown.match(tableRegex);

        if (tableMatches && tableMatches.length > 0) {
          // Use the first table found and clean it up
          const tableText = tableMatches[0].trim();

          // Split into lines and process each line
          const lines = tableText.split('\n');
          const cleanedLines: string[] = [];

          lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
              cleanedLines.push(trimmedLine);

              // Add separator row after the first row (header) if it doesn't exist
              if (index === 0 && lines.length > 1) {
                const nextLine = lines[index + 1]?.trim();
                if (!nextLine || !nextLine.match(/^\|[\s\-:]+\|$/)) {
                  // Count the number of columns to create separator
                  const columnCount = (trimmedLine.match(/\|/g) || []).length - 1;
                  const separator = '| ' + Array(columnCount).fill('---').join(' | ') + ' |';
                  cleanedLines.push(separator);
                }
              }
            }
          });

          markdownText = cleanedLines.join('\n');
        }
      }

      // Fallback: if no raw markdown or table not found, generate markdown from table
      if (!markdownText) {
        const rows = tableElement.querySelectorAll('tr');
        const markdownRows: string[] = [];

        rows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll('th, td');
          const cellTexts: string[] = [];

          cells.forEach((cell) => {
            // Get text content and clean it up, escape pipe characters
            const text = cell.textContent?.replace(/\s+/g, ' ').trim().replace(/\|/g, '\\|') || '';
            cellTexts.push(text);
          });

          // Create markdown table row
          const rowText = '| ' + cellTexts.join(' | ') + ' |';
          markdownRows.push(rowText);

          // Add separator row after header (first row)
          if (rowIndex === 0 && row.querySelector('th')) {
            const separatorCells = cellTexts.map(() => '---');
            const separatorRow = '| ' + separatorCells.join(' | ') + ' |';
            markdownRows.push(separatorRow);
          }
        });

        markdownText = markdownRows.join('\n');
      }

      // Create a styled version of the table HTML for clipboard with minimal padding
      const styledTableHTML = tableHTML
        .replace(
          /<table[^>]*>/i,
          '<table style="border-collapse: collapse; border: 1px solid #000; font-family: Arial, sans-serif; font-size: 11px;">',
        )
        .replace(
          /<th[^>]*>/gi,
          '<th style="border: 1px solid #000; padding: 2px 4px; background-color: #f0f0f0; font-weight: bold; text-align: left; line-height: 1.2;">',
        )
        .replace(
          /<td[^>]*>/gi,
          '<td style="border: 1px solid #000; padding: 2px 4px; line-height: 1.2;">',
        );

      // Try to copy both HTML and plain text to clipboard
      if (navigator.clipboard && navigator.clipboard.write) {
        // Modern clipboard API that supports multiple formats
        const clipboardItems = [
          new ClipboardItem({
            'text/html': new Blob([styledTableHTML], { type: 'text/html' }),
            'text/plain': new Blob([markdownText], { type: 'text/plain' }),
          }),
        ];

        await navigator.clipboard.write(clipboardItems);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        // Fallback to plain text only
        await navigator.clipboard.writeText(markdownText);
      } else {
        throw new Error('Clipboard API not supported');
      }
    } catch (error) {
      console.error('Failed to copy table:', error);

      // Fallback for older browsers using document.execCommand
      try {
        // Create a temporary container
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '-9999px';

        // Clone the table and clean it up for copying
        const clonedTable = tableElement.cloneNode(true) as HTMLTableElement;

        // Remove the copy button from the cloned table
        const copyButton = clonedTable.querySelector('.table-copy-button');
        if (copyButton) {
          copyButton.remove();
        }

        // Style the cloned table for better copying
        clonedTable.style.borderCollapse = 'collapse';
        clonedTable.style.border = '1px solid #000';

        // Style all cells with minimal padding for compact pasting
        const allCells = clonedTable.querySelectorAll('th, td');
        allCells.forEach((cell) => {
          (cell as HTMLElement).style.border = '1px solid #000';
          (cell as HTMLElement).style.padding = '2px 4px';
          (cell as HTMLElement).style.lineHeight = '1.2';
        });

        container.appendChild(clonedTable);
        document.body.appendChild(container);

        // Select the content
        const range = document.createRange();
        range.selectNodeContents(clonedTable);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        // Copy to clipboard
        const success = document.execCommand('copy');

        // Clean up
        selection?.removeAllRanges();
        document.body.removeChild(container);

        if (success) {
          console.log('Table copied to clipboard (fallback with formatting)');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);

        // Final fallback - plain text only
        try {
          const textArea = document.createElement('textarea');

          // Generate markdown as final fallback
          const rows = tableElement.querySelectorAll('tr');
          const markdownRows: string[] = [];

          rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('th, td');
            const cellTexts: string[] = [];

            cells.forEach((cell) => {
              const text =
                cell.textContent?.replace(/\s+/g, ' ').trim().replace(/\|/g, '\\|') || '';
              cellTexts.push(text);
            });

            const rowText = '| ' + cellTexts.join(' | ') + ' |';
            markdownRows.push(rowText);

            if (rowIndex === 0 && row.querySelector('th')) {
              const separatorCells = cellTexts.map(() => '---');
              const separatorRow = '| ' + separatorCells.join(' | ') + ' |';
              markdownRows.push(separatorRow);
            }
          });

          textArea.value = markdownRows.join('\n') || 'Failed to copy table';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (finalError) {
          console.error('All copy methods failed:', finalError);
        }
      }
    }
  };

  // Helper function to parse markdown-style text and convert to JSX
  const parseMarkdownText = (text: string, sourceBtns?: React.ReactNode) => {
    if (!text) return null;

    // Split by lines for processing
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    lines.forEach((line, index) => {
      // Skip empty lines at the beginning but preserve spacing between content
      if (line.trim() === '') return;

      // Headers (## or ###)
      if (line.startsWith('### ')) {
        const headerText = line.replace(/^### \*\*(.*?)\*\*$/, '$1').replace(/^### /, '');
        elements.push(
          <h4
            key={`h4-${key++}`}
            style={{
              fontSize: '13px',
              fontWeight: 'bold',
              margin: '12px 0 8px 0',
              color: '#333',
            }}
          >
            {headerText}
          </h4>,
        );
      } else if (line.startsWith('## ')) {
        const headerText = line.replace(/^## \*\*(.*?)\*\*$/, '$1').replace(/^## /, '');
        elements.push(
          <h3
            key={`h3-${key++}`}
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              margin: '20px 0 20px 0',
              color: '#333',
            }}
          >
            {headerText}
          </h3>,
        );
      }
      // Horizontal rule
      else if (line.trim() === '---') {
        elements.push(
          <hr
            key={`hr-${key++}`}
            style={{
              margin: '16px 0',
              border: 'none',
              borderTop: '1px solid #ddd',
            }}
          />,
        );
      }
      // Bullet points
      else if (line.startsWith('- ')) {
        const bulletText = line.substring(2);
        const formattedText = processBoldText(bulletText);

        // Check if this is the last non-empty line
        const isLastLine =
          index === lines.length - 1 || lines.slice(index + 1).every((l) => l.trim() === '');

        elements.push(
          <div
            key={`bullet-${key++}`}
            style={{
              marginLeft: '20px',
              marginBottom: '4px',
              position: 'relative',
              display: isLastLine && sourceBtns ? 'inline-block' : 'block',
            }}
          >
            <FiberManualRecord
              style={{
                position: 'absolute',
                left: '-18px',
                top: '2px',
                fontSize: '8px',
                color: '#666',
              }}
            />
            {formattedText}
            {isLastLine && sourceBtns && <span style={{ marginLeft: '4px' }}>{sourceBtns}</span>}
          </div>,
        );
      }
      // Regular text
      else {
        const formattedText = processBoldText(line);
        if (formattedText) {
          // Check if this is the last non-empty line
          const isLastLine =
            index === lines.length - 1 || lines.slice(index + 1).every((l) => l.trim() === '');

          elements.push(
            <div
              key={`text-${key++}`}
              style={{
                marginBottom: '4px',
                display: isLastLine && sourceBtns ? 'inline-block' : 'block',
              }}
            >
              {formattedText}
              {isLastLine && sourceBtns && <span style={{ marginLeft: '4px' }}>{sourceBtns}</span>}
            </div>,
          );
        }
      }
    });

    return <div>{elements}</div>;
  };

  // Helper function to process bold text (**text**)
  const processBoldText = (text: string) => {
    if (!text) return null;

    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return (
          <strong key={`bold-${index}`} style={{ fontWeight: 'bold' }}>
            {boldText}
          </strong>
        );
      }
      return part;
    });
  };

  const handleItemClick = (item: any) => {
    if (!fileSidebarAskAI) {
      history.push(
        history.location.pathname.replace(/\/files.*/, ``) +
          `/files/${item.fileId || item.file.id}`,
      );

      // Check if we already have the file details in cache
      const cacheKey = `${caseId}-${item.fileId}`;
      const cachedFileDetails = fileDetailsCache.current[cacheKey];

      if (cachedFileDetails) {
        // Use cached data instead of making API call
        dispatch(setCurrentSelectedFile(cachedFileDetails));
      } else {
        // Make API call and cache the result
        dispatch(fetchFileDetails({ caseId, fileId: item.fileId, firstTrialBookOnly: true })).then(
          (res: any) => {
            // Store the result in cache for future use
            fileDetailsCache.current[cacheKey] = res;
            dispatch(setCurrentSelectedFile(res));
          },
        );
      }

      // Always create a new location object to ensure Redux state changes
      const newLocation = {
        ...item.location,
        // Add a timestamp to ensure the object is always new
        timestamp: Date.now(),
      };
      dispatch(setDocSearch(item.highlight ? item.highlight : item.text, newLocation));
    } else {
      // Use the same dispatch approach for fileSidebarAskAI context
      const newLocation = {
        ...item.location,
        // Add a timestamp to ensure the object is always new
        timestamp: Date.now(),
      };

      if (gridView) {
        dispatch(setDocSearch(item.highlight ? item.highlight : item.text, newLocation));
      } else if (viewerControl && typeof viewerControl.scrollToAsync === 'function') {
        try {
          // Import the highlighting logic from utils
          const highlightLocation = (loc: any) => {
            if (!loc) return;
            // Clear existing navigation highlights with better error handling
            try {
              if (typeof viewerControl.getAllMarks === 'function') {
                const allMarks = viewerControl.getAllMarks() || [];
                const toRemove = allMarks.filter((m: any) => {
                  try {
                    const dt = m.getData('subType');
                    return dt === 'highlightEvent' || dt === 'markButtonTrigger';
                  } catch {
                    return false;
                  }
                });

                if (toRemove.length && typeof viewerControl.deleteMarks === 'function') {
                  // Force hide mark buttons immediately before deleting marks
                  toRemove.forEach((mark: any) => {
                    try {
                      // Dispatch a custom event to force hide mark buttons
                      if (mark.getData && mark.getData('subType') === 'markButtonTrigger') {
                        const event = new CustomEvent('forceHideMarkButtons', {
                          detail: { markId: mark.uid },
                        });
                        window.dispatchEvent(event);
                      }
                    } catch (e) {
                      console.warn('Error dispatching hide event for mark:', mark.uid, e);
                    }
                  });

                  viewerControl.deleteMarks(toRemove);
                }
              }
            } catch (e) {
              console.warn('Error clearing existing navigation highlights:', e);
            }
            // Scroll and highlight
            viewerControl
              .scrollToAsync({ pageNumber: loc.pageNumber, x: loc.x, y: loc.y })
              .then(() => viewerControl.requestPageText(loc.pageNumber))
              .then(() => {
                // Enhanced mark creation with page readiness check
                const createMarkWithRetry = (attemptCount = 0) => {
                  const maxAttempts = 5;

                  if (attemptCount >= maxAttempts) {
                    console.warn('Failed to create mark after maximum attempts:', loc);
                    return;
                  }

                  // Check if the page is ready for coordinate conversion
                  const isPageReady = () => {
                    try {
                      // Try to convert a basic coordinate to test if the page is ready
                      const testCoord = viewerControl.convertPageToWindowCoordinates(
                        loc.pageNumber,
                        { x: loc.x, y: loc.y },
                      );
                      return (
                        testCoord &&
                        testCoord.clientX !== undefined &&
                        testCoord.clientY !== undefined
                      );
                    } catch (e) {
                      return false;
                    }
                  };

                  if (!isPageReady()) {
                    // Wait longer between retries as attempts increase
                    const delay = 200 * Math.pow(1.5, attemptCount);
                    setTimeout(() => createMarkWithRetry(attemptCount + 1), delay);
                    return;
                  }

                  // Check for overlaps with persistent marks only (only prevent if >95% overlap)
                  let hasOverlappingPersistentMark = false;
                  try {
                    if (typeof viewerControl.getAllMarks === 'function') {
                      const allMarks = viewerControl.getAllMarks() || [];
                      const persistentPageMarks = allMarks.filter((mark: any) => {
                        try {
                          if (!mark || typeof mark.getPageNumber !== 'function') return false;
                          if (mark.getPageNumber() !== loc.pageNumber) return false;
                          const subType =
                            typeof mark.getData === 'function' ? mark.getData('subType') : null;
                          const isNav =
                            subType === 'highlightEvent' || subType === 'markButtonTrigger';
                          return !isNav;
                        } catch {
                          return false;
                        }
                      });
                      hasOverlappingPersistentMark = persistentPageMarks.some((mark: any) => {
                        try {
                          const pos = mark.getPosition();
                          if (!pos || pos.startIndex === undefined || pos.length === undefined)
                            return false;
                          const markStart = pos.startIndex;
                          const markEnd = markStart + pos.length;
                          const newStart = loc.startIndex;
                          const newEnd = newStart + loc.textLength;
                          const overlapStart = Math.max(markStart, newStart);
                          const overlapEnd = Math.min(markEnd, newEnd);
                          const overlapLength = Math.max(0, overlapEnd - overlapStart);
                          if (overlapLength > 0) {
                            return overlapLength / loc.textLength > 0.95;
                          }
                        } catch {
                          return false;
                        }
                        return false;
                      });
                    }
                  } catch {}

                  if (hasOverlappingPersistentMark) {
                    return;
                  }

                  // Determine subType based on context - use markButtonTrigger when we want to show action buttons directly
                  let shouldShowMarkButton = false;

                  // Check if location object has explicit context information
                  if (loc.showMarkButton !== undefined) {
                    shouldShowMarkButton = loc.showMarkButton;
                  } else {
                    // Fallback to URL-based detection for AI tools
                    shouldShowMarkButton = window.location.href.includes('ai-tools');
                  }

                  const subType = shouldShowMarkButton ? 'markButtonTrigger' : 'highlightEvent';

                  try {
                    // Create highlight mark
                    const area = viewerControl
                      .addMark(loc.pageNumber, 'HighlightAnnotation')
                      .setPosition({ startIndex: loc.startIndex, length: loc.textLength })
                      .setData('subType', subType);
                    area.setFillColor('#AFD0F9');
                  } catch (error) {
                    console.error('Error creating mark:', error);
                  }
                };

                // Start the mark creation process with initial delay
                // Add extra delay to ensure mark clearing is complete
                setTimeout(() => createMarkWithRetry(), 150);
              })
              .catch((err: any) =>
                console.error('SearchAnswerContainer: scroll/request highlight error', err),
              );
          };

          // Call the highlight function directly
          // Add showMarkButton flag for fileSidebarAskAI context (similar to DocumentSearch)
          const enhancedLocation = {
            ...newLocation,
            showMarkButton: fileSidebarAskAI || window.location.href.includes('ai-tools'),
          };
          highlightLocation(enhancedLocation);
        } catch (error) {
          console.error('SearchAnswerContainer: direct highlighting failed:', error);
        }
      }
    }
  };
  const getSourcesBtns = (refrences: any, lineIndex: number, itemText: string) => {
    return refrences.map(
      (src: any, srcIdx: number) =>
        (src.source || src) <= allMatchesCount && (
          <React.Fragment key={srcIdx}>
            (
            <Tooltip
              title={
                allMatches && allMatches[src.source - 1] ? (
                  <div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: highlightText(
                          allMatches[src.source - 1].text,
                          src.highlight || allMatches[src.source - 1].highlight || '',
                        ),
                      }}
                    />
                    <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                      <div>
                        File:{' '}
                        {allMatches[src.source - 1].fileName ||
                          allMatches[src.source - 1].filename ||
                          allMatches[src.source - 1].file?.name}
                      </div>
                      <div>
                        ID:{' '}
                        {allMatches[src.source - 1].fileId || allMatches[src.source - 1].file?.id}
                      </div>
                    </div>
                  </div>
                ) : (
                  `Source ${src.source || src}`
                )
              }
              placement="top"
              arrow
            >
              <Button
                size="small"
                sx={{
                  p: 0,
                  minWidth: '0.5rem',
                  fontSize: isTranscriptPage ? '1.3rem' : '1rem',
                  fontWeight: clickedSource === `${lineIndex}-${src.source || src}` ? 700 : 600,
                  textDecoration:
                    clickedSource === `${lineIndex}-${src.source || src}` ? 'underline' : 'none',
                  textDecorationColor:
                    clickedSource === `${lineIndex}-${src.source || src}`
                      ? '#233473'
                      : 'transparent',
                  textDecorationThickness:
                    clickedSource === `${lineIndex}-${src.source || src}` ? '2px' : '1px',
                  color: '#233473',
                }}
                variant="text"
                onClick={(e) => {
                  selectText(lineIndex);
                  // Set the clicked source to show visual feedback
                  setClickedSource(`${lineIndex}-${src.source || src}`);

                  // Handle direct navigation instead of opening menu
                  if (allMatches && allMatches[src.source - 1]) {
                    const match = {
                      ...allMatches[src.source - 1],
                      srcIndex: src.source || src,
                      highlight: src.highlight || allMatches[src.source - 1].highlight || '',
                    };
                    handleItemClick(match);
                  } else if (handleMatchesLinkClicked) {
                    // Fallback to old behavior if needed
                    handleMatchesLinkClicked(e, src.source || src, allMatches, src.highlight || '');
                  }
                }}
              >
                {src.source || src}
              </Button>
            </Tooltip>
            )
          </React.Fragment>
        ),
    );
  };

  // Alternative method to render text with Markdown-compatible source links
  const renderTextWithSourceButtons = (text: string) => {
    // Avoid processing if text already contains markdown links (prevent double processing)
    if (
      text.includes('](#source-') ||
      text.includes('](#expand-sources-') ||
      text.includes('](#collapse-sources-')
    ) {
      return text;
    }

    // Match patterns like (Text175, Text176) or (175, 176) or single (5)
    // More comprehensive regex to handle various formats
    const regex = /\((?:Text)?(\d+(?:\s*,\s*(?:Text)?\d+)*)\)/g;

    return text.replace(regex, (match, numbersText) => {
      // Extract numbers from the match - handle both "Text123" and "123" formats
      const numbers = numbersText.match(/\d+/g);
      if (!numbers) {
        return match;
      }

      // Create a unique key for this source group - use a stable identifier
      const sortedNumbers = [...numbers].sort((a, b) => parseInt(a) - parseInt(b));
      const sourceGroupKey = `sources-${sortedNumbers.join('-')}`;
      const isExpanded = expandedSources.has(sourceGroupKey);

      // Show first 3 or all sources based on expansion state
      const sourcesToShow = isExpanded ? numbers : numbers.slice(0, 3);
      const remaining = isExpanded ? [] : numbers.slice(3);

      // Create Markdown links for sources to show
      const markdownLinks = sourcesToShow.map((num: string) => {
        const sourceNum = parseInt(num, 10);

        // Check if we have match data for tooltip-like behavior
        if (allMatches && allMatches[sourceNum - 1]) {
          const matchData = allMatches[sourceNum - 1];
          const fileName =
            matchData.fileName || matchData.filename || matchData.file?.name || 'Unknown';
          const fileId = matchData.fileId || matchData.file?.id || 'Unknown';

          // Create a Markdown link with title attribute for hover effect
          return `[${sourceNum}](#source-${sourceNum} "${fileName} - ID: ${fileId}")`;
        } else {
          // Fallback for sources without match data
          return `[${sourceNum}](#source-${sourceNum} "Source ${sourceNum}")`;
        }
      });

      // Add "+X more" link if there are more than 3 sources and not expanded
      if (remaining.length > 0) {
        markdownLinks.push(
          `[+${remaining.length} More ](#expand-sources-${sourceGroupKey} "Click to see ${remaining.length} more sources")`,
        );
      }

      // Return the Markdown links joined with commas, wrapped in parentheses
      let result;
      if (isExpanded && numbers.length > 3) {
        // Add collapse icon with space but no comma
        const hiddenCount = numbers.length - 3;
        result = `(${markdownLinks.join(', ')} [⇇](#collapse-sources-${sourceGroupKey} "Click to hide ${hiddenCount} sources"))`;
      } else {
        result = `(${markdownLinks.join(', ')})`;
      }

      return result;
    });
  };
  const renderRawText = (text: string) => {
    // Process the text with source references based on current expansion state
    const processedText = renderTextWithSourceButtons(text);

    // Custom link renderer to handle source clicks
    const customLinkRenderer = ({ href, children, title }: any) => {
      // Check if this is an "expand sources" link
      if (href && href.startsWith('#expand-sources-')) {
        const sourceGroupKey = href.replace('#expand-sources-', '');

        return (
          <Button
            size="small"
            sx={{
              p: '0 5px',
              minWidth: '0.5rem',
              fontSize: isTranscriptPage ? '1.3rem' : '1rem',
              fontWeight: 600,
              color: '#4567E5',
              textTransform: 'none', // Prevent uppercase transformation
              '&:hover': {
                backgroundColor: '#E5E5E5',
              },
            }}
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              setExpandedSources((prev) => {
                const newSet = new Set(prev);
                newSet.add(sourceGroupKey);
                return newSet;
              });
            }}
          >
            {children}
          </Button>
        );
      }

      // Check if this is a "collapse sources" link
      if (href && href.startsWith('#collapse-sources-')) {
        const sourceGroupKey = href.replace('#collapse-sources-', '');

        return (
          <Button
            size="small"
            sx={{
              p: '0 5px',
              minWidth: '0.5rem',
              fontSize: isTranscriptPage ? '1.3rem' : '1rem',
              fontWeight: 600,
              color: '#4567E5',
              textTransform: 'none', // Prevent uppercase transformation
              '&:hover': {
                backgroundColor: '#E5E5E5',
              },
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
            }}
            variant="text"
            onClick={(e) => {
              e.preventDefault();
              setExpandedSources((prev) => {
                const newSet = new Set(prev);
                newSet.delete(sourceGroupKey);
                return newSet;
              });
            }}
          >
            {children?.toString().includes('⇇') ? (
              <KeyboardDoubleArrowLeft sx={{ fontSize: 'inherit' }} />
            ) : (
              children
            )}
          </Button>
        );
      }

      // Check if this is a regular source link
      if (href && href.startsWith('#source-')) {
        const sourceNum = parseInt(href.replace('#source-', ''), 10);

        // Check if location is valid (not empty, null, undefined, or empty object {})
        const matchData = allMatches && allMatches[sourceNum - 1];
        const isLocationValid =
          matchData &&
          matchData.location &&
          !(typeof matchData.location === 'object' && Object.keys(matchData.location).length === 0);

        // Create a unique identifier for this source button
        const sourceId = `markdown-${sourceNum}`;

        return (
          <Tooltip
            title={
              allMatches && allMatches[sourceNum - 1] ? (
                <div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: highlightText(
                        allMatches[sourceNum - 1].text,
                        allMatches[sourceNum - 1].highlight || '',
                      ),
                    }}
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                    <div>
                      File:{' '}
                      {allMatches[sourceNum - 1].fileName ||
                        allMatches[sourceNum - 1].filename ||
                        allMatches[sourceNum - 1].file?.name}
                    </div>
                    <div>
                      ID: {allMatches[sourceNum - 1].fileId || allMatches[sourceNum - 1].file?.id}
                    </div>
                  </div>
                </div>
              ) : (
                `Source ${sourceNum}`
              )
            }
            placement="top"
            arrow
          >
            <Button
              size="small"
              sx={{
                p: 0,
                minWidth: '0.5rem',
                fontSize: isTranscriptPage ? '1.3rem' : '1rem',
                fontWeight: clickedSource === sourceId ? 700 : 600,
                color: isLocationValid
                  ? clickedSource === sourceId
                    ? '#233473'
                    : '#4567E5'
                  : '#B3B3B3',
                textTransform: 'none', // Prevent uppercase transformation
                textDecoration: clickedSource === sourceId ? 'underline' : 'none',
                textDecorationColor: clickedSource === sourceId ? '#233473' : 'transparent',
                textDecorationThickness: clickedSource === sourceId ? '2px' : '1px',
                '&:hover': {
                  backgroundColor: isLocationValid ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                },
                cursor: isLocationValid ? 'pointer' : 'default',
                '&:disabled': {
                  color: '#ccc',
                },
              }}
              variant="text"
              onClick={(e) => {
                e.preventDefault();
                // Only handle source click if location is valid
                if (isLocationValid) {
                  // Set the clicked source to show visual feedback
                  setClickedSource(sourceId);

                  if (allMatches && allMatches[sourceNum - 1]) {
                    const match = {
                      ...allMatches[sourceNum - 1],
                      srcIndex: sourceNum,
                      highlight: allMatches[sourceNum - 1].highlight || '',
                    };
                    handleItemClick(match);
                  } else if (handleMatchesLinkClicked) {
                    handleMatchesLinkClicked(e, sourceNum, allMatches, '');
                  }
                }
              }}
            >
              {children}
            </Button>
          </Tooltip>
        );
      }

      // Default link rendering for other links
      return (
        <a href={href} title={title}>
          {children}
        </a>
      );
    };

    // Custom table components for styling
    const customTableRenderer = ({ children }: any) => {
      return (
        <div className="table-container">
          <IconButton
            className="table-copy-button"
            size="small"
            onClick={() => {
              const tableElement = document.getElementById(tableId) as HTMLTableElement;
              if (tableElement) {
                copyTableToClipboard(tableElement, text);
              }
            }}
            sx={{
              width: 28,
              height: 28,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Tooltip title={T.translate('viewing.copyTable')} placement="top">
              <ContentCopy sx={{ fontSize: 16, color: '#666' }} />
            </Tooltip>
          </IconButton>
          <table
            id={tableId}
            className="markdown-table"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: '16px',
              marginBottom: '16px',
              border: '1px solid #ddd',
            }}
          >
            {children}
          </table>
        </div>
      );
    };

    const customTableHeadRenderer = ({ children }: any) => (
      <thead style={{ backgroundColor: '#f8f9fa' }}>{children}</thead>
    );

    const customTableBodyRenderer = ({ children }: any) => <tbody>{children}</tbody>;

    const customTableRowRenderer = ({ children }: any) => (
      <tr
        style={{
          borderBottom: '1px solid #ddd',
        }}
      >
        {children}
      </tr>
    );

    const customTableHeaderCellRenderer = ({ children }: any) => (
      <th
        style={{
          fontWeight: 'bold',
          padding: '12px 8px',
          textAlign: 'left',
          borderRight: '1px solid #ddd',
          backgroundColor: '#f8f9fa',
        }}
      >
        {children}
      </th>
    );

    const customTableCellRenderer = ({ children }: any) => (
      <td
        style={{
          padding: '10px 8px',
          borderRight: '1px solid #ddd',
        }}
      >
        {children}
      </td>
    );

    // Custom list renderers to reduce padding-left
    const customUnorderedListRenderer = ({ children }: any) => (
      <ul style={{ paddingLeft: '0.5rem', marginLeft: '1.6rem' }}>{children}</ul>
    );

    const customOrderedListRenderer = ({ children }: any) => (
      <ol style={{ paddingLeft: '0.5rem', marginLeft: '1.6rem' }}>{children}</ol>
    );

    const customDescriptionListRenderer = ({ children }: any) => (
      <dl style={{ paddingLeft: '0.5rem', marginLeft: '1.6rem' }}>{children}</dl>
    );

    return (
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: customLinkRenderer,
          table: customTableRenderer,
          thead: customTableHeadRenderer,
          tbody: customTableBodyRenderer,
          tr: customTableRowRenderer,
          th: customTableHeaderCellRenderer,
          td: customTableCellRenderer,
          ul: customUnorderedListRenderer,
          ol: customOrderedListRenderer,
          dl: customDescriptionListRenderer,
        }}
      >
        {processedText}
      </Markdown>
    );
  };
  return (
    <div
      style={{
        lineHeight: 1.4,
        marginBottom: 0,
        paddingBottom: 0,
        position: 'relative',
      }}
    >
      <>
        {rawAnswer ? (
          renderRawText(rawAnswer)
        ) : (
          <div>
            {itemsToRender.map((item: any, ind: number) => (
              <div
                key={ind}
                style={{
                  marginBottom: ind < itemsToRender.length - 1 ? '12px' : '0',
                  paddingBottom: 0,
                  lineHeight: 'inherit',
                  wordWrap: 'break-word', // Handle long text better
                  overflowWrap: 'break-word',
                }}
              >
                {typingEffect ? (
                  <TypingEffect
                    responseRef={textRefs.current[ind]}
                    response={item.text}
                    typingSpeed={10}
                    scrollBarRef={scrollBarRef}
                    elementsAfterText={getSourcesBtns(
                      item.references || item.sources,
                      ind,
                      item.text,
                    )}
                  />
                ) : (
                  <>
                    <div ref={textRefs.current[ind]} style={{ display: 'block' }}>
                      {parseMarkdownText(
                        item.text,
                        getSourcesBtns(item.references || item.sources, ind, item.text),
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
            {hasMoreItems && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '8px 8px 8px 0',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                Showing first {MAX_ITEMS_TO_RENDER} of {response.length} items.
                {response.length - MAX_ITEMS_TO_RENDER} more items are available.
              </div>
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default React.memo(SearchAnswerContainer);
